// src/components/GalleryPanel.jsx
import React, { useCallback, useRef, useState, useEffect } from "react";
import heic2any from "heic2any";
import exifr from "exifr";
import {
    ref as storageRef,
    getDownloadURL,
    deleteObject,
} from "firebase/storage";
import { firebaseApp, auth, db, storage, isFirebaseConfigured } from "../firebase";
import { collection, query, where, orderBy, onSnapshot, deleteDoc, doc as docRef, getDocs, limit } from "firebase/firestore";
import { uploadFileAndSaveMeta } from "../utils/storageUploads";
import ImageModal from "../components/ImageModal";
import "./css/GalleryPanel.css";

// ---------- STORAGE URL CACHE (with TTL + negative-cache) ----------
const STORAGE_URL_CACHE_KEY = "jift_storage_url_cache_v1";
// TTL for successful cache entries (ms) — e.g. 7 days
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
// Cooldown when resolution failed (ms) — e.g. 5 minutes
const FAILED_COOLDOWN_MS = 5 * 60 * 1000;

const storageUrlCache = new Map(); // key -> { url, ts } OR { failedUntil }

let persistTimer = null;
function persistCacheToLocalStorageDebounced() {
    if (persistTimer) clearTimeout(persistTimer);
    persistTimer = setTimeout(() => {
        try {
            // convert map to plain object
            const obj = {};
            for (const [k, v] of storageUrlCache.entries()) {
                obj[k] = v;
            }
            if (typeof window !== "undefined" && window.localStorage) {
                localStorage.setItem(STORAGE_URL_CACHE_KEY, JSON.stringify(obj));
            }
        } catch (e) {
            // ignore localStorage errors
        }
        persistTimer = null;
    }, 500); // 500ms debounce
}

// hydrate cache from localStorage (best-effort, only in browser)
try {
    if (typeof window !== "undefined" && window.localStorage) {
        const raw = localStorage.getItem(STORAGE_URL_CACHE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === "object") {
                Object.entries(parsed).forEach(([k, v]) => {
                    if (k && v) storageUrlCache.set(k, v);
                });
            }
        }
    }
} catch (e) {
    // ignore localStorage errors
}


function persistCacheToLocalStorage() {
    try {
        const obj = Object.fromEntries(storageUrlCache);
        localStorage.setItem(STORAGE_URL_CACHE_KEY, JSON.stringify(obj));
    } catch (e) { /* ignore */ }
}

const useFirebase = Boolean(isFirebaseConfigured && firebaseApp && auth && db && storage);

// CHANGEABLE: maximum media items allowed per trip
const MAX_MEDIA_PER_TRIP = 10;

function uniqueId(prefix = "id") {
    try {
        if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
            return `${prefix}-${crypto.randomUUID()}`;
        }
    } catch (e) { }
    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function dedupeMediaArray(arr = []) {
    const seen = new Set();
    const out = [];
    for (const it of arr) {
        const key = it?.id ? `id:${it.id}` : (it?.url ? `url:${it.url}` : uniqueId("tmp"));
        if (!seen.has(key)) {
            seen.add(key);
            out.push(it);
        }
    }
    return out;
}

async function isHeicFile(file) {
    const lower = (file.name || "").toLowerCase();
    return (file.type && (file.type.includes("heif") || file.type.includes("heic"))) || lower.endsWith(".heic") || lower.endsWith(".heif");
}

async function convertHeicForPreview(file) {
    try {
        // read ArrayBuffer for exifr and for conversion
        const ab = await file.arrayBuffer();

        // read metadata (exifr supports HEIC container metadata)
        let metadata = {};
        try {
            metadata = await exifr.parse(ab, { tiff: true, ifd0: true, exif: true, gps: true });
        } catch (metaErr) {
            console.warn("exifr parse failed:", metaErr);
        }

        // convert to jpeg blob (heic2any returns a Blob)
        const convertedBlob = await heic2any({
            blob: new Blob([ab]),
            toType: "image/jpeg",
            quality: 0.92, // adjust if you want smaller files
        });

        return { blob: convertedBlob, metadata, mime: "image/jpeg" };
    } catch (err) {
        console.error("HEIC conversion error", err);
        throw err;
    }
}

/**
 * Resolve a persisted item's latest Storage download URL when possible.
 * Uses in-memory cache + localStorage to avoid repeated getDownloadURL calls.
 * Returns the item possibly extended with url.
 */
async function resolveLatestStorageUrl(item) {
    if (!item || !storage) return item;
    if (!item.storagePath) return item;

    const path = item.storagePath;
    const now = Date.now();

    // check cache entry
    const entry = storageUrlCache.get(path);
    if (entry) {
        // negative cache: we recently failed, skip attempts until failedUntil
        if (entry.failedUntil && now < entry.failedUntil) {
            return item;
        }
        // valid success entry: check TTL
        if (entry.url && entry.ts && (now - entry.ts) < CACHE_TTL_MS) {
            return { ...item, url: entry.url };
        }
        // expired entry: fall through to attempt resolution
    }

    // attempt to resolve (one-time). If it fails, set a failedUntil to avoid hammering.
    try {
        const sref = storageRef(storage, path);
        const url = await getDownloadURL(sref);
        if (url) {
            storageUrlCache.set(path, { url, ts: Date.now() });
            try { persistCacheToLocalStorageDebounced(); } catch (e) { /* ignore */ }
            return { ...item, url };
        }
    } catch (err) {
        // set negative-cache to avoid retrying for FAILED_COOLDOWN_MS
        storageUrlCache.set(path, { failedUntil: Date.now() + FAILED_COOLDOWN_MS });
        try { persistCacheToLocalStorageDebounced(); } catch (e) { /* ignore */ }
        // log once (already in your code style)
        console.warn("resolveLatestStorageUrl failed for", path, err?.message || err);
        return item;
    }
    return item;
}

export default function GalleryPanel({
    media = [],
    setMedia = () => { },
    isPublicView = false,
    selectedTrip = null,
    user = null,
    setToast = () => { },
}) {
    const fileInputRef = useRef(null);
    const [dragActive, setDragActive] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [progressMap, setProgressMap] = useState({});
    const [modalMedia, setModalMedia] = useState(null);
    const mediaUnsubRef = useRef(null);
    const mediaLoadTokenRef = useRef(0);
    const isOwner = Boolean(
        user &&
        selectedTrip &&
        (String(user.uid) === String(selectedTrip.ownerId || selectedTrip.owner_id || selectedTrip.ownerUid || selectedTrip.owner || ""))
    );

    // Helper: count media items (persisted + previews)
    const getCurrentMediaCount = useCallback((mediaArr) => {
        if (!Array.isArray(mediaArr)) return 0;
        return mediaArr.length;
    }, []);

    // Determine remaining slots
    const currentCount = getCurrentMediaCount(media);
    const remainingSlots = Math.max(0, MAX_MEDIA_PER_TRIP - currentCount);

    // Clear local-only previews when switching trips — ensures we only show storage-backed images.
    useEffect(() => {
        setMedia((prev) => (Array.isArray(prev) ? prev.filter((m) => !m.file) : []));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTrip?.trip_id]);

    useEffect(() => {
        const tripId = selectedTrip?.trip_id;
        // cleanup previous subscription immediately if trip changed
        try {
            if (mediaUnsubRef.current) {
                try { mediaUnsubRef.current(); } catch (e) { /* ignore */ }
                mediaUnsubRef.current = null;
            }
        } catch (e) { /* ignore */ }

        // if no trip or no db, clear persisted items and exit
        if (!tripId || !db) {
            setMedia((prev) => (Array.isArray(prev) ? prev.filter((m) => m.file) : []));
            return;
        }

        let mounted = true;
        // increment token to invalidate previous async tasks
        const myToken = ++mediaLoadTokenRef.current;

        const mediaCol = collection(db, "trips", tripId, "media");
        let q;
        try {
            q = query(mediaCol, orderBy("uploadedAt", "desc"), limit(200));
        } catch (err) {
            q = query(mediaCol);
        }

        // Helper to convert a Firestore doc into your persisted item (same logic you had)
        const docToItem = async (d) => {
            const data = d.data() || {};
            const uploadedAt =
                data.uploadedAt && typeof data.uploadedAt.toDate === "function"
                    ? data.uploadedAt.toDate().toISOString()
                    : data.uploadedAt || null;

            const base = {
                id: d.id,
                name: data.name || data.title || "file",
                url: data.url || data.storageUrl || data.fullUrl || "",
                thumbnailUrl: data.thumbnailUrl || data.thumbUrl || data.previewUrl || "",
                type: data.type || (data.mime && data.mime.startsWith("video") ? "video" : "image"),
                date: data.date || uploadedAt || null,
                gps: data.gps || null,
                storagePath: data.storagePath || data.path || null,
                uploadedBy: data.uploadedBy || data.uploader || null,
                uploadedAt,
                _persisted: true,
            };

            const hasUsableUrl = (base.thumbnailUrl && typeof base.thumbnailUrl === "string" && !base.thumbnailUrl.startsWith("blob:")) ||
                (base.url && typeof base.url === "string" && !base.url.startsWith("blob:"));

            if (hasUsableUrl) return base;
            if (base.storagePath) {
                try {
                    return await resolveLatestStorageUrl(base);
                } catch (e) {
                    return base;
                }
            }
            return base;
        };

        // FAST initial load via getDocs (but bail if token invalidated)
        (async () => {
            try {
                const snap = await getDocs(q);
                // if unmounted or token changed, ignore this result
                if (!mounted || mediaLoadTokenRef.current !== myToken) return;

                const docsToProcess = snap.docs || [];
                const initialPersisted = await Promise.all(docsToProcess.map(docToItem));

                if (!mounted || mediaLoadTokenRef.current !== myToken) return;

                setMedia((prev) => {
                    const previews = Array.isArray(prev) ? prev.filter((m) => m.file) : [];
                    return dedupeMediaArray([...initialPersisted, ...previews]);
                });
            } catch (err) {
                console.warn("Initial media fetch failed", err);
            }
        })();

        // Attach a single onSnapshot for live updates
        const unsub = onSnapshot(
            q,
            async (snap) => {
                try {
                    if (!mounted || mediaLoadTokenRef.current !== myToken) return;

                    let docsToProcess = snap.docs;
                    if (!docsToProcess || docsToProcess.length === 0) {
                        // fallback attempt once if snapshot empty
                        try {
                            const one = await getDocs(mediaCol);
                            if (one && !one.empty) docsToProcess = one.docs;
                        } catch (e) { /* non-fatal */ }
                    }

                    if (!mounted || mediaLoadTokenRef.current !== myToken) return;

                    const persisted = await Promise.all(docsToProcess.map(docToItem));
                    if (!mounted || mediaLoadTokenRef.current !== myToken) return;

                    setMedia((prev) => {
                        const previews = Array.isArray(prev) ? prev.filter((m) => m.file) : [];
                        return dedupeMediaArray([...persisted, ...previews]);
                    });

                    // schedule retries for missing urls (your existing retry backoff logic but guarded)
                    const itemsToRetry = persisted.filter(it => it && it.storagePath && !it.url && !it.thumbnailUrl);
                    if (itemsToRetry.length > 0) {
                        const retryResolveMissingUrls = async (items, attempt = 1) => {
                            if (!mounted || mediaLoadTokenRef.current !== myToken) return;
                            const delayMs = attempt === 1 ? 2000 : (attempt === 2 ? 6000 : 20000);
                            await new Promise(r => setTimeout(r, delayMs));
                            if (!mounted || mediaLoadTokenRef.current !== myToken) return;

                            const reResolved = await Promise.all(items.map(async (it) => {
                                try { return await resolveLatestStorageUrl(it); } catch (e) { return it; }
                            }));

                            if (!mounted || mediaLoadTokenRef.current !== myToken) return;

                            setMedia((prev) => {
                                const arr = Array.isArray(prev) ? [...prev] : [];
                                for (const rr of reResolved) {
                                    if (rr && (rr.url || rr.thumbnailUrl)) {
                                        const idx = arr.findIndex(x => x.id === rr.id || x.storagePath === rr.storagePath);
                                        if (idx >= 0) arr[idx] = { ...arr[idx], ...rr };
                                        else arr.unshift(rr);
                                    }
                                }
                                return dedupeMediaArray(arr);
                            });

                            const stillMissing = reResolved.filter(x => x && x.storagePath && !x.url && !x.thumbnailUrl);
                            if (stillMissing.length > 0 && attempt < 3) {
                                retryResolveMissingUrls(stillMissing, attempt + 1);
                            }
                        };

                        retryResolveMissingUrls(itemsToRetry, 1);
                    }
                } catch (procErr) {
                    console.error("Error processing media snapshot:", procErr);
                }
            },
            (err) => {
                console.error("media onSnapshot failed", err);
                try {
                    setToast({ msg: "Live media sync error", type: "warning" });
                    setTimeout(() => setToast(null), 2000);
                } catch (e) { /* ignore */ }
            }
        );

        // store unsub ref so we can cancel proactively if trip changes
        mediaUnsubRef.current = () => {
            try { unsub && unsub(); } catch (e) { /* ignore */ }
            mediaUnsubRef.current = null;
        };

        // cleanup handler for this effect
        return () => {
            mounted = false;
            // invalidate async tasks
            mediaLoadTokenRef.current++;
            try { if (mediaUnsubRef.current) { mediaUnsubRef.current(); } } catch (e) { /* ignore */ }
        };
        // only re-run when tripId or db changes
    }, [selectedTrip?.trip_id, db]);

    // pushFilesToPreview now enforces MAX_MEDIA_PER_TRIP
    const pushFilesToPreview = useCallback(
        async (files) => {
            const fileList = Array.from(files || []);
            if (fileList.length === 0) return;

            // compute how many we can accept
            const existing = Array.isArray(media) ? media : [];
            const existingCountLocal = existing.length;
            const remaining = Math.max(0, MAX_MEDIA_PER_TRIP - existingCountLocal);

            if (remaining <= 0) {
                try {
                    setToast({ msg: `You can upload a maximum of ${MAX_MEDIA_PER_TRIP} items for this trip.`, type: "warning" });
                    setTimeout(() => setToast(null), 2500);
                } catch (e) { }
                return;
            }

            let filesToProcess = fileList;
            if (fileList.length > remaining) {
                filesToProcess = fileList.slice(0, remaining);
                try {
                    setToast({ msg: `Only ${remaining} of the selected files were accepted (max ${MAX_MEDIA_PER_TRIP}).`, type: "info" });
                    setTimeout(() => setToast(null), 3000);
                } catch (e) { }
            }

            const created = await Promise.all(filesToProcess.map(async (file) => {
                try {
                    if (await isHeicFile(file)) {
                        const { blob, metadata, mime } = await convertHeicForPreview(file);
                        const url = URL.createObjectURL(blob);
                        return {
                            id: uniqueId("preview"),
                            type: "image",
                            url,
                            name: file.name.replace(/\.(heic|heif)$/i, ".jpg"),
                            date: metadata?.DateTimeOriginal || new Date().toISOString(),
                            file: new File([blob], file.name.replace(/\.(heic|heif)$/i, ".jpg"), { type: mime }),
                            uploaded: false,
                            uploadError: false,
                            gps: metadata?.latitude && metadata?.longitude ? { lat: metadata.latitude, lng: metadata.longitude } : null,
                        };
                    } else {
                        // non-HEIC: as before
                        return {
                            id: uniqueId("preview"),
                            type: file.type && file.type.startsWith("video") ? "video" : "image",
                            url: URL.createObjectURL(file),
                            name: file.name,
                            date: new Date().toISOString(),
                            file,
                            uploaded: false,
                            uploadError: false,
                            gps: null,
                        };
                    }
                } catch (e) {
                    // fallback to basic preview if convert fails
                    return {
                        id: uniqueId("preview"),
                        type: file.type && file.type.startsWith("video") ? "video" : "image",
                        url: URL.createObjectURL(file),
                        name: file.name,
                        date: new Date().toISOString(),
                        file,
                        uploaded: false,
                        uploadError: false,
                        gps: null,
                    };
                }
            }));

            setMedia((m) => {
                const prevArr = Array.isArray(m) ? m : [];
                return dedupeMediaArray([...created, ...prevArr]);
            });
        },
        // include media so remaining calc uses latest value
        [setMedia, media, setToast]
    );

    const handleMediaSelected = (e) => {
        const files = Array.from(e.target.files || []);
        pushFilesToPreview(files);
        if (e.target) e.target.value = "";
    };

    const onDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        const dt = e.dataTransfer;
        if (!dt) return;
        const files = Array.from(dt.files || []);
        pushFilesToPreview(files);
    };
    const onDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(true);
    };
    const onDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(true);
    };
    const onDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
    };

    // OPEN MODAL: lazy-resolve full url only when needed.
    const openModalFor = async (m) => {
        if (!m) return;
        // If preview/local file, open directly
        if (m.file) {
            setModalMedia(m);
            return;
        }

        // If persisted and already has a fullUrl or url, set and open
        if (m._persisted && (m.fullUrl || m.url)) {
            const resolved = { ...m, url: m.fullUrl || m.url };
            // cache it as well
            if (m.storagePath && resolved.url) {
                storageUrlCache.set(m.storagePath, resolved.url);
                try { persistCacheToLocalStorage(); } catch (e) { }
            }
            setModalMedia(resolved);
            return;
        }

        // If thumbnail exists, show that first (fast), then try to resolve full url once in background.
        if (m.thumbnailUrl) {
            setModalMedia(m);
            // attempt to resolve full url once (non-blocking)
            (async () => {
                try {
                    const resolved = await resolveLatestStorageUrl(m);
                    if (resolved && resolved.url) {
                        // update modal with full url if modal still open for same media id
                        setModalMedia((current) => (current && current.id === m.id ? { ...current, url: resolved.url } : current));
                        setMedia((prev) => {
                            if (!Array.isArray(prev)) return prev;
                            return dedupeMediaArray(prev.map(x => (x.id === m.id ? { ...x, url: resolved.url } : x)));
                        });
                    }
                } catch (_) { /* ignore */ }
            })();
            return;
        }

        // Last-resort: try to resolve once (blocking fallback)
        try {
            const resolved = await resolveLatestStorageUrl(m);
            setModalMedia(resolved);
        } catch (e) {
            setModalMedia(m);
        }
    };

    async function uploadSelected() {
        if (!isOwner) return;
        const tripId = selectedTrip?.trip_id;
        if (!tripId) return;

        // Validate again on upload: compute how many persisted items are already in storage (best-effort)
        const persistedCount = Array.isArray(media) ? media.filter(m => m._persisted).length : 0;
        const toUpload = media.filter((m) => m.file && !m.uploaded && !m.uploadError);

        // If uploading these would exceed limit, trim or abort
        const remainingForUpload = Math.max(0, MAX_MEDIA_PER_TRIP - persistedCount);
        if (remainingForUpload <= 0) {
            try {
                setToast({ msg: `Cannot upload: maximum of ${MAX_MEDIA_PER_TRIP} items already present.`, type: "warning" });
                setTimeout(() => setToast(null), 2500);
            } catch (e) { }
            return;
        }

        const toUploadTrimmed = toUpload.slice(0, remainingForUpload);
        if (toUploadTrimmed.length < toUpload.length) {
            try {
                setToast({ msg: `Only ${toUploadTrimmed.length} files will be uploaded to respect the ${MAX_MEDIA_PER_TRIP} limit.`, type: "info" });
                setTimeout(() => setToast(null), 3000);
            } catch (e) { }
        }

        if (!toUploadTrimmed.length) return;

        setUploading(true);

        for (const item of toUploadTrimmed) {
            const id = item.id;
            setProgressMap((p) => ({ ...p, [id]: 0 }));
            setMedia((prev) => (Array.isArray(prev) ? prev.map((x) => (x.id === id ? { ...x, uploading: true } : x)) : prev));

            try {
                // uploadFileAndSaveMeta is expected to upload and write Firestore doc with thumbnailUrl/fullUrl when possible.
                const final = await uploadFileAndSaveMeta(item.file, tripId, user, (pct) => {
                    setProgressMap((p) => ({ ...p, [id]: pct }));
                });

                if (final && final._firestorePersisted === false) {
                    setToast({ msg: `Upload saved to storage but failed to persist metadata: ${final._firestoreError || "unknown"}`, type: "warning" });
                    setTimeout(() => setToast(null), 3000);
                    setMedia((prev) => (Array.isArray(prev) ? prev.map((x) => (x.id === id ? { ...x, uploading: false, uploadError: true, _debug: final } : x)) : prev));
                    continue;
                }

                // final should include storagePath and/or url; prefer thumbnailUrl if present.
                const persistedItem = {
                    id: final.id || uniqueId("persisted"),
                    name: final.name || item.name,
                    url: final.fullUrl || final.url || final.storageUrl || "",
                    thumbnailUrl: final.thumbnailUrl || final.thumbUrl || "",
                    type: final.type || item.type || "image",
                    date: final.date || new Date().toISOString(),
                    gps: final.gps || item.gps || null,
                    storagePath: final.storagePath || final.path || final.storagePath,
                    uploadedBy: final.uploadedBy || user?.uid || null,
                    uploadedAt: final.uploadedAt || new Date().toISOString(),
                    _persisted: true,
                };

                // Resolve latest storage url only if necessary (rare if upload returned urls)
                const resolved = (persistedItem.url || persistedItem.thumbnailUrl) ? persistedItem : await resolveLatestStorageUrl(persistedItem);

                setMedia((prev) => {
                    const arr = Array.isArray(prev) ? [...prev] : [];
                    const idxById = arr.findIndex((x) => x.id === id);

                    if (idxById >= 0) {
                        arr[idxById] = { ...arr[idxById], ...resolved, uploading: false, uploaded: true, file: undefined };
                        try {
                            if (arr[idxById].url && arr[idxById].url.startsWith("blob:")) URL.revokeObjectURL(arr[idxById].url);
                        } catch (e) { }
                    } else {
                        const idxByUrl = arr.findIndex((x) => x.url === resolved.url || x.url === resolved.storageUrl || (x.url && x.url === item.url));
                        if (idxByUrl >= 0) {
                            arr[idxByUrl] = { ...arr[idxByUrl], ...resolved, uploading: false, uploaded: true, file: undefined };
                            try {
                                if (arr[idxByUrl].url && arr[idxByUrl].url.startsWith("blob:")) URL.revokeObjectURL(arr[idxByUrl].url);
                            } catch (e) { }
                        } else {
                            arr.unshift({ ...resolved, uploading: false, uploaded: true });
                        }
                    }

                    return dedupeMediaArray(arr);
                });
            } catch (err) {
                console.error("Upload failed:", err);
                setMedia((prev) => (Array.isArray(prev) ? prev.map((x) => (x.id === id ? { ...x, uploading: false, uploadError: true } : x)) : prev));
                setToast({ msg: "Upload failed", type: "warning" });
                setTimeout(() => setToast(null), 2000);
            } finally {
                setProgressMap((p) => {
                    const c = { ...p };
                    delete c[id];
                    return c;
                });
            }
        }

        setUploading(false);
    }

    const pendingCount = (Array.isArray(media) ? media.filter((m) => m.file && !m.uploaded && !m.uploadError) : []).length;

    // Helper: extract storage path from a firebase download URL (best-effort)
    function extractPathFromFirebaseDownloadUrl(downloadUrl) {
        if (!downloadUrl || typeof downloadUrl !== "string") return null;
        try {
            // firebase download URLs are like:
            // https://firebasestorage.googleapis.com/v0/b/<bucket>/o/<encodedPath>?alt=media&token=...
            const m = downloadUrl.match(/\/o\/([^?]+)/);
            if (!m || !m[1]) return null;
            return decodeURIComponent(m[1]);
        } catch (e) {
            return null;
        }
    }

    // Robust removeMedia that deletes both main object and thumbnail object (if any)
    const removeMedia = async (mediaIdOrObj) => {
        const item =
            typeof mediaIdOrObj === "string"
                ? media.find((m) => m.id === mediaIdOrObj) || { id: mediaIdOrObj }
                : mediaIdOrObj;

        if (!item) return;
        const tripId = selectedTrip?.trip_id;
        if (!tripId) {
            console.warn("No trip selected");
            return;
        }

        const isOwnerLocal =
            user &&
            (selectedTrip?.ownerId === user.uid ||
                selectedTrip?.owner_id === user.uid ||
                selectedTrip?.owner === user.uid);
        if (!isOwnerLocal) {
            try {
                setToast?.({ msg: "You don't have permission to remove this media", type: "warning" });
                setTimeout(() => setToast?.(null), 1600);
            } catch (e) { }
            return;
        }

        try {
            let deletedAny = false;

            // 1) Try direct delete by id if item._persisted && item.id looks like Firestore doc id
            if (item.id && item._persisted) {
                try {
                    const docRefToDelete = docRef(db, "trips", tripId, "media", item.id);
                    await deleteDoc(docRefToDelete);
                    deletedAny = true;
                } catch (docErr) {
                    console.warn("Direct delete by id failed (may not exist):", docErr?.message || docErr);
                }
            }

            // 2) Fallback: find docs by storagePath or url
            if (!deletedAny && item._persisted && (item.storagePath || item.url)) {
                try {
                    const mediaCol = collection(db, "trips", tripId, "media");
                    const conds = [];
                    if (item.storagePath) conds.push({ field: "storagePath", value: item.storagePath });
                    if (item.url) conds.push({ field: "url", value: item.url });

                    // try storagePath first, then url
                    let snaps = null;
                    if (item.storagePath) {
                        const q = query(mediaCol, where("storagePath", "==", item.storagePath));
                        snaps = await getDocs(q);
                    }
                    if ((!snaps || snaps.empty) && item.url) {
                        const q2 = query(mediaCol, where("url", "==", item.url));
                        snaps = await getDocs(q2);
                    }

                    if (snaps && !snaps.empty) {
                        const deletions = [];
                        snaps.forEach((d) => {
                            deletions.push(deleteDoc(docRef(db, "trips", tripId, "media", d.id)).catch(e => {
                                console.warn("Failed to delete matched doc", d.id, e);
                            }));
                            deletedAny = true;
                        });
                        await Promise.all(deletions);
                    } else {
                        console.warn("No matching media docs found for fallback delete (storagePath/url).");
                    }
                } catch (qErr) {
                    console.warn("Fallback query/delete failed:", qErr?.message || qErr);
                }
            }

            // 3) Delete Storage objects: primary + thumbnail (if present)
            const storagePathsToTry = new Set();

            if (item.storagePath) storagePathsToTry.add(item.storagePath);
            if (item.thumbnailPath) storagePathsToTry.add(item.thumbnailPath);

            // if thumbnailPath not present but thumbnailUrl is, try to extract path
            if (!item.thumbnailPath && item.thumbnailUrl) {
                const extracted = extractPathFromFirebaseDownloadUrl(item.thumbnailUrl);
                if (extracted) storagePathsToTry.add(extracted);
            }

            // if storagePath not present but url is, try to extract it
            if (!item.storagePath && item.url) {
                const extracted = extractPathFromFirebaseDownloadUrl(item.url);
                if (extracted) storagePathsToTry.add(extracted);
            }

            // Also handle legacy "storageUrl" / "fullUrl" etc
            if (item.storageUrl && !storagePathsToTry.has(item.storageUrl)) {
                const extracted = extractPathFromFirebaseDownloadUrl(item.storageUrl);
                if (extracted) storagePathsToTry.add(extracted);
            }
            if (item.fullUrl && !storagePathsToTry.has(item.fullUrl)) {
                const extracted = extractPathFromFirebaseDownloadUrl(item.fullUrl);
                if (extracted) storagePathsToTry.add(extracted);
            }

            if (storagePathsToTry.size > 0) {
                await Promise.all(Array.from(storagePathsToTry).map(async (path) => {
                    if (!path) return;
                    try {
                        const sref = storageRef(storage, path);
                        await deleteObject(sref);
                    } catch (sErr) {
                        console.warn("deleteObject failed for", path, sErr?.message || sErr);
                    }
                }));
            }

            // revoke blob URLs if preview
            if (item && item.file && item.url && item.url.startsWith && item.url.startsWith("blob:")) {
                try { URL.revokeObjectURL(item.url); } catch (e) { /* ignore */ }
            }

            // Remove from local UI state (match by id, url OR storagePath OR thumbnailPath)
            setMedia((prev) =>
            (Array.isArray(prev)
                ? prev.filter((x) => {
                    if (!x) return false;
                    if (item.id && x.id === item.id) return false;
                    if (item.url && x.url === item.url) return false;
                    if (item.storagePath && x.storagePath === item.storagePath) return false;
                    if (item.thumbnailPath && x.thumbnailPath === item.thumbnailPath) return false;
                    return true;
                })
                : prev)
            );

            // Remove storagePath(s) from cache
            try {
                if (item.storagePath) {
                    storageUrlCache.delete(item.storagePath);
                }
                if (item.thumbnailPath) {
                    storageUrlCache.delete(item.thumbnailPath);
                }
                // also try remove any extracted paths stored earlier
                if (item.thumbnailUrl) {
                    const p = extractPathFromFirebaseDownloadUrl(item.thumbnailUrl);
                    if (p) storageUrlCache.delete(p);
                }
                if (item.url) {
                    const p2 = extractPathFromFirebaseDownloadUrl(item.url);
                    if (p2) storageUrlCache.delete(p2);
                }
                try { persistCacheToLocalStorageDebounced(); } catch (e) { /* ignore */ }

                setToast?.({ msg: "Media removed", type: "success" });
                setTimeout(() => setToast?.(null), 1400);
            } catch (e) { /* ignore */ }
        } catch (err) {
            console.error("removeMedia failed", err);
            try {
                setToast?.({ msg: "Failed to remove media", type: "warning" });
                setTimeout(() => setToast?.(null), 1600);
            } catch (e) { }
        }
    };

    // cleanup blob URLs for previews when component unmounts
    useEffect(() => {
        return () => {
            try {
                (Array.isArray(media) ? media : []).forEach((m) => {
                    if (m && m.url && m.url.startsWith && m.url.startsWith("blob:")) {
                        try {
                            URL.revokeObjectURL(m.url);
                        } catch (e) { }
                    }
                });
            } catch (e) { }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Whether upload UI should be disabled due to limit reached
    const limitReached = remainingSlots <= 0;

    return (
        <section className="gallery-section">
            <h2 className="gallery-title">
                Gallery (Photos Or Videos)
                {isOwner ? (
                    <span className="gallery-info">
                        — Allowed: {MAX_MEDIA_PER_TRIP}, Current: {currentCount}, Remaining: {remainingSlots}
                    </span>
                ) : null}
            </h2>
            {isOwner ? (
                <>
                    <div
                        className={`dropzone ${dragActive ? "drag-active" : ""} ${uploading ? "uploading" : ""} ${limitReached ? "disabled" : ""}`}
                        onDrop={limitReached ? (e) => { e.preventDefault(); e.stopPropagation(); try { setToast?.({ msg: `Maximum of ${MAX_MEDIA_PER_TRIP} items reached`, type: "warning" }); setTimeout(() => setToast?.(null), 2000); } catch { } } : onDrop}
                        onDragOver={limitReached ? (e) => { e.preventDefault(); e.stopPropagation(); } : onDragOver}
                        onDragEnter={limitReached ? (e) => { e.preventDefault(); e.stopPropagation(); } : onDragEnter}
                        onDragLeave={onDragLeave}
                        onClick={() => { if (!limitReached && fileInputRef.current) fileInputRef.current.click(); }}
                        aria-label="Drop files here to add previews"
                        role="button"
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*,video/*"
                            multiple
                            onChange={handleMediaSelected}
                            className="file-input-hidden"
                            disabled={uploading || limitReached}
                        />
                        <div className="dropzone-text">
                            {limitReached ? `Limit reached (${MAX_MEDIA_PER_TRIP}). Remove existing items to add more.` : (uploading ? "Uploading…" : dragActive ? "Drop files to add previews" : "Drag & drop photos or videos here to add previews — or click to choose files")}
                        </div>
                        <div className="dropzone-muted">
                            Files are added as previews; click <strong>Upload Selected</strong> to persist to Firebase Storage & Firestore.
                        </div>
                    </div>

                    {pendingCount > 0 && (
                        <div className="controls">
                            <button className="btn-start" onClick={uploadSelected} disabled={uploading} title={`Upload ${pendingCount} file(s)`}>
                                {uploading ? "Uploading…" : `Upload Selected (${pendingCount})`}
                            </button>

                            <button
                                className="btn-cancel"
                                onClick={() => {
                                    setMedia((m) => {
                                        const prevArr = Array.isArray(m) ? m : [];
                                        prevArr.forEach((it) => {
                                            if (it && it.file && it.url && it.url.startsWith && it.url.startsWith("blob:")) {
                                                try {
                                                    URL.revokeObjectURL(it.url);
                                                } catch (e) { }
                                            }
                                        });
                                        return prevArr.filter((x) => !x.file);
                                    });
                                }}
                                disabled={uploading}
                            >
                                Clear Previews
                            </button>
                        </div>
                    )}
                </>
            ) : (
                <div className="muted info">{isPublicView ? null : <div className="sign-in-msg">Sign in as the trip owner to add uploads.</div>}</div>
            )}

            <div className="gallery">
                {(Array.isArray(media) ? media : []).map((m) => (
                    <div key={m.id} className="gallery-item">
                        <button
                            type="button"
                            onClick={() => openModalFor(m)}
                            className="thumb-button"
                            title="Open preview"
                            style={{ border: 0, padding: 0, background: "transparent", cursor: "pointer" }}
                        >
                            {m.type === "image" ? (
                                (() => {
                                    // prefer remote thumbnail URL (non-blob), else use blob preview; otherwise no src
                                    const thumbSrc =
                                        (m.thumbnailUrl && !m.thumbnailUrl.startsWith("blob:") ? m.thumbnailUrl :
                                            (m.url && m.url.startsWith("blob:") ? m.url : null)
                                        );

                                    if (!thumbSrc) {
                                        // don't render an <img> with empty src — show a placeholder (or null)
                                        return (
                                            <div className="media-thumb placeholder" aria-hidden="true">
                                                <span className="placeholder-icon">🖼</span>
                                            </div>
                                        );
                                    }

                                    return (
                                        <img
                                            src={thumbSrc}
                                            alt={m.name || "media"}
                                            className="media-thumb"
                                            loading="lazy"
                                            decoding="async"
                                        />
                                    );
                                })()
                            ) : (
                                (() => {
                                    // For videos: prefer a thumbnail URL (remote) for poster; fallback to blob video src for small previews.
                                    const thumbSrc =
                                        (m.thumbnailUrl && !m.thumbnailUrl.startsWith("blob:") ? m.thumbnailUrl :
                                            (m.url && m.url.startsWith("blob:") ? m.url : null)
                                        );

                                    if (!thumbSrc) {
                                        return (
                                            <div className="media-thumb placeholder" aria-hidden="true">
                                                <span className="placeholder-icon">🎞</span>
                                            </div>
                                        );
                                    }

                                    // Use <video> only when we actually have a playable src. Otherwise, show poster image
                                    const isVideoBlob = m.url && m.url.startsWith("blob:");
                                    if (isVideoBlob) {
                                        return <video src={m.url} controls={false} className="media-thumb" />;
                                    }

                                    // Show thumbnail image as poster for videos
                                    return <img src={thumbSrc} alt={m.name || "video thumbnail"} className="media-thumb" loading="lazy" decoding="async" />;
                                })()
                            )}

                        </button>

                        {isOwner && (
                            <button onClick={() => removeMedia(m.id)} className="delete-btn" title="Remove">
                                ✕
                            </button>
                        )}
                    </div>
                ))}

                {(!Array.isArray(media) || media.length === 0) && <div className="muted empty-placeholder">No media</div>}

                <ImageModal open={Boolean(modalMedia)} media={modalMedia} onClose={() => setModalMedia(null)} />
            </div>
        </section>
    );
}
